import {
  DELTA_DELETED,
  extractDeltaPaths,
  findLinesForPaths,
  LIST_DELTA_MARKER,
  statusDisplay,
} from "./ConfigStaging.support";
import { actionsForStatus, parseEditedData } from "./ConfigStagingPage.support";
import {
  CloseRounded as CloseIcon,
  OpenInNewRounded as OpenInNewIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Link as MuiLink,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { readableDate } from "common/dates";
import CodeEditor, { DiffEditor } from "components/admin/CodeEditor";
import Button from "components/common/Button";
import DefaultLoader from "components/common/DefaultLoader";
import IconButton from "components/common/IconButton";
import { useEffect, useMemo, useState } from "react";
import {
  useGetStagedConfigQuery,
  useApproveStagedConfigMutation,
  useRejectStagedConfigMutation,
  useUpdateStagedConfigMutation,
} from "services/adminService";

const ACTOR_FIELDS = [
  { label: "Staged", by: "stagedBy", at: "stagedAt" },
  { label: "Edited", by: "editedBy", at: "editedAt" },
  { label: "Approved", by: "approvedBy", at: "approvedAt" },
  { label: "Rejected", by: "rejectedBy", at: "rejectedAt" },
  { label: "Applied", by: "appliedBy", at: "appliedAt" },
];

const ActorLines = ({ record }) => (
  <Stack direction={"row"} gap={2} flexWrap={"wrap"}>
    {ACTOR_FIELDS.filter(({ by, at }) => record[by] || record[at]).map(
      ({ label, by, at }) => (
        <Typography key={label} variant={"caption"} color={"text.secondary"}>
          {`${label} by ${
            record[by]?.email ?? "unknown"
          } on ${readableDate(record[at], { includeTime: true })}`}
        </Typography>
      )
    )}
  </Stack>
);

const MetadataHeader = ({ record }) => {
  const status = statusDisplay(record.status);
  return (
    <Stack gap={1} flexShrink={0}>
      <Stack direction={"row"} gap={1} alignItems={"center"} flexWrap={"wrap"}>
        <Chip label={status.label} color={status.color} size={"small"} />
        {record.stale && (
          <Chip label={"Updated config"} color={"warning"} size={"small"} />
        )}
        <Typography variant={"subtitle2"}>{record.key}</Typography>
        <Typography variant={"subtitle2"} color={"text.secondary"}>
          {record.companyId}
        </Typography>
        {(record.prNumbers ?? []).map((prNumber) => (
          <Chip key={prNumber} label={`#${prNumber}`} size={"small"} />
        ))}
        {record.githubLink && (
          <MuiLink
            href={record.githubLink}
            target={"_blank"}
            rel={"noopener noreferrer"}
            underline={"hover"}
            sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
          >
            {"GitHub"}
            <OpenInNewIcon sx={{ fontSize: "1rem" }} />
          </MuiLink>
        )}
      </Stack>
      <Typography variant={"body2"}>{record.reason}</Typography>
      <ActorLines record={record} />
      {record.status === "failed" && record.failureReason && (
        <Alert severity={"error"}>
          {"Apply failed: "}
          {record.failureReason}
        </Alert>
      )}
      {record.stale && (
        <Alert severity={"warning"}>
          {
            "The live config has been updated since this change was staged. On apply, the staged delta is applied on top of the current config — check the Apply preview."
          }
        </Alert>
      )}
    </Stack>
  );
};

const VIEWS = [
  { value: "preview", label: "Apply preview" },
  { value: "staged", label: "Staged delta" },
  { value: "delta", label: "Delta" },
];

const VIEW_HINTS = {
  preview: "What the live config will look like if this delta is applied now.",
  staged:
    "The change as its author staged it, against the config of that moment.",
  delta: "The raw delta this change applies — removed keys are marked.",
};

const StagedChangeDetail = ({ open, onClose, companyId, id }) => {
  const [editing, setEditing] = useState(false);
  const [editedText, setEditedText] = useState("");
  const [actionError, setActionError] = useState(null);
  const [chosenView, setChosenView] = useState(null);
  const [showDrift, setShowDrift] = useState(true);

  const { data: record, isLoading, isError } = useGetStagedConfigQuery(
    { companyId, id },
    { skip: !open }
  );

  const [
    approveStagedConfig,
    { isLoading: isApproving },
  ] = useApproveStagedConfigMutation();
  const [
    rejectStagedConfig,
    { isLoading: isRejecting },
  ] = useRejectStagedConfigMutation();
  const [
    updateStagedConfig,
    { isLoading: isSaving },
  ] = useUpdateStagedConfigMutation();

  useEffect(() => {
    if (open) {
      setEditing(false);
      setActionError(null);
      setChosenView(null);
    }
  }, [open, companyId, id]);

  const runAction = async (mutation, closeAfter) => {
    setActionError(null);
    try {
      await mutation({ companyId, id }).unwrap();
      if (closeAfter) onClose();
    } catch (error) {
      setActionError(error?.data?.message || error?.message || "Action failed");
    }
  };

  const startEditing = () => {
    setEditedText(JSON.stringify(record?.data, null, 2));
    setActionError(null);
    setEditing(true);
  };

  const parsed = editing ? parseEditedData(editedText) : null;

  const saveEdit = async () => {
    if (!parsed?.valid) return;
    setActionError(null);
    try {
      await updateStagedConfig({ companyId, id, data: parsed.data }).unwrap();
      setEditing(false);
    } catch (error) {
      setActionError(error?.data?.message || error?.message || "Save failed");
    }
  };

  const actions = actionsForStatus(record?.status);
  const actionInFlight = isApproving || isRejecting || isSaving;
  const isTerminal = ["applied", "rejected"].includes(record?.status);
  const view = chosenView ?? (isTerminal ? "staged" : "preview");
  const asText = (value) =>
    value == null ? "" : JSON.stringify(value, null, 2);
  const liveText = asText(record?.currentData);
  const previewText = asText(record?.previewData);
  const baseText = asText(record?.baseData);
  const stagedText = asText(record?.data);
  const deltaText = asText(record?.delta);
  const driftPaths = useMemo(() => extractDeltaPaths(record?.driftDelta), [
    record,
  ]);
  const hasDrift = driftPaths.length > 0;
  const driftLines = useMemo(() => {
    if (view !== "preview" || !showDrift || !hasDrift) {
      return { original: [], modified: [] };
    }
    return {
      original: findLinesForPaths(liveText, driftPaths),
      modified: findLinesForPaths(previewText, driftPaths),
    };
  }, [view, showDrift, hasDrift, liveText, previewText, driftPaths]);
  const paneLabels =
    view === "preview"
      ? {
          left:
            record?.currentData == null
              ? "Live config (none exists for this key)"
              : "Live config (now)",
          right: "After apply",
        }
      : {
          left:
            record?.baseData != null
              ? "Config when staged"
              : record?.baseVersionId != null
              ? "Config when staged (not captured — staged before delta support)"
              : "Config when staged (none existed)",
          right: "Staged change",
        };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        style: {
          width: "90vw",
          height: "90vh",
          maxWidth: "90vw",
          maxHeight: "90vh",
          borderRadius: 12,
        },
      }}
    >
      <DialogTitle>
        <Stack
          direction={"row"}
          alignItems={"center"}
          justifyContent={"space-between"}
        >
          <Typography variant={"h6"}>
            {record?.title ?? "Staged change"}
          </Typography>
          <IconButton icon={CloseIcon} tooltip={"Close"} onClick={onClose} />
        </Stack>
      </DialogTitle>
      <DialogContent
        sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
      >
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flex: 1,
            }}
          >
            <DefaultLoader size={48} />
          </Box>
        ) : isError || !record ? (
          <Alert severity={"error"}>{"Failed to load staged change."}</Alert>
        ) : (
          <>
            <MetadataHeader record={record} />
            {actionError && <Alert severity={"error"}>{actionError}</Alert>}
            {editing && parsed && !parsed.valid && (
              <Alert severity={"error"}>
                {"Invalid JSON: "}
                {parsed.error}
              </Alert>
            )}
            {!editing && (
              <Stack
                direction={"row"}
                gap={2}
                flexShrink={0}
                alignItems={"center"}
                justifyContent={"space-between"}
              >
                <Stack direction={"row"} gap={2} alignItems={"center"}>
                  <ToggleButtonGroup
                    value={view}
                    exclusive
                    size={"small"}
                    onChange={(_, value) => {
                      if (value) setChosenView(value);
                    }}
                  >
                    {VIEWS.map(({ value, label }) => (
                      <ToggleButton key={value} value={value}>
                        {label}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                  {view === "preview" && hasDrift && (
                    <FormControlLabel
                      control={
                        <Switch
                          size={"small"}
                          checked={showDrift}
                          onChange={(e) => setShowDrift(e.target.checked)}
                        />
                      }
                      label={
                        <Stack
                          direction={"row"}
                          gap={0.75}
                          alignItems={"center"}
                        >
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: 0.5,
                              backgroundColor: "rgba(25, 118, 210, 0.5)",
                              border: "1px solid #1976d2",
                            }}
                          />
                          <Typography variant={"caption"}>
                            {"Underlying changes since staging"}
                          </Typography>
                        </Stack>
                      }
                    />
                  )}
                </Stack>
                <Typography variant={"caption"} color={"text.secondary"}>
                  {VIEW_HINTS[view]}
                </Typography>
              </Stack>
            )}
            <Stack
              direction={"row"}
              gap={2}
              flexShrink={0}
              justifyContent={"space-between"}
            >
              {editing ? (
                <Typography variant={"caption"} color={"text.secondary"}>
                  {"Staged change (editing)"}
                </Typography>
              ) : view === "delta" ? (
                <Typography variant={"caption"} color={"text.secondary"}>
                  {`Delta document — "${DELTA_DELETED}" removes a key; a "${LIST_DELTA_MARKER}" entry edits array items in place by index`}
                </Typography>
              ) : (
                <>
                  <Typography variant={"caption"} color={"text.secondary"}>
                    {paneLabels.left}
                  </Typography>
                  <Typography variant={"caption"} color={"text.secondary"}>
                    {paneLabels.right}
                  </Typography>
                </>
              )}
            </Stack>
            <Box sx={{ flex: 1, minHeight: 0 }}>
              {editing ? (
                <CodeEditor
                  value={editedText}
                  language={"json"}
                  onChange={(value) => setEditedText(value ?? "")}
                  height={"100%"}
                />
              ) : view === "delta" ? (
                <CodeEditor
                  value={deltaText}
                  language={"json"}
                  readOnly
                  height={"100%"}
                />
              ) : (
                <DiffEditor
                  original={view === "preview" ? liveText : baseText}
                  modified={view === "preview" ? previewText : stagedText}
                  language={"json"}
                  height={"100%"}
                  originalHighlightLines={driftLines.original}
                  modifiedHighlightLines={driftLines.modified}
                />
              )}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        {editing ? (
          <>
            <Button
              onClick={saveEdit}
              isLoading={isSaving}
              isDisabled={!parsed?.valid}
            >
              {"Save"}
            </Button>
            <Button
              onClick={() => setEditing(false)}
              isDisabled={isSaving}
              color={"secondary"}
            >
              {"Cancel"}
            </Button>
          </>
        ) : (
          record && (
            <>
              {actions.approve && (
                <Button
                  onClick={() => runAction(approveStagedConfig, true)}
                  isLoading={isApproving}
                  isDisabled={actionInFlight}
                >
                  {"Approve"}
                </Button>
              )}
              {actions.reject && (
                <Button
                  onClick={() => runAction(rejectStagedConfig, true)}
                  isLoading={isRejecting}
                  isDisabled={actionInFlight}
                  color={"error"}
                >
                  {"Reject"}
                </Button>
              )}
              {actions.edit && (
                <Button
                  onClick={startEditing}
                  isDisabled={actionInFlight}
                  color={"secondary"}
                >
                  {"Edit"}
                </Button>
              )}
            </>
          )
        )}
      </DialogActions>
    </Dialog>
  );
};

export default StagedChangeDetail;
